import React from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import useHomepageVersion from "../../hooks/useHomepageVersion";
import {
  HOMEPAGE_SWITCH_ACTION,
  HOMEPAGE_VERSION,
  fireHomepageGtm,
} from "../../utils/homepageVersion";
import { selectDarkMode } from "../../Redux/Reducers/darkModeSlice";
import styles from "./HomepageVersionSwitch.module.css";

/**
 * Turns the new homepage on and off. Renders nothing unless the student can
 * actually choose (paid, standalone Sandbox), so callers can drop it in without
 * their own gating.
 *
 * A state toggle, not an action: both ends keep their words and the knob moves
 * between them, landing on the one that is on. The banner names the experience
 * (HOMEPAGE_SWITCH_NAME) on the switch's left and the switch carries "Switch to
 * old version" on its right, so the knob sits left while the new homepage is
 * showing and crosses right once the student is on the old one.
 *
 * Switching from anywhere but /home takes the student to /home, so the change is
 * visible rather than silently stored.
 *
 * @param {Object} props
 * @param {"banner"|"sidebar"|"mobile_menu"} props.source - where it was used, for tracking
 * @param {"inline"|"stacked"} [props.layout="stacked"] - inline: switch beside the label
 *   (banner); stacked: switch above a centred label (side nav)
 * @param {boolean} [props.compact=false] - stacked only: fold the label away (collapsed
 *   sidebar). The label stays mounted so it can grow back in step with the drawer.
 * @param {() => void} [props.onSwitched] - called after switching, e.g. to close a drawer
 */
const HomepageVersionSwitch = ({
  source,
  layout = "stacked",
  compact = false,
  onSwitched,
}) => {
  const { isNewHomepage, isChoiceAvailable, setHomepageVersion } =
    useHomepageVersion();
  const isDarkMode = useSelector(selectDarkMode);
  const history = useHistory();
  const location = useLocation();

  if (!isChoiceAvailable) return null;

  const label = HOMEPAGE_SWITCH_ACTION;

  const handleSwitch = () => {
    const next = isNewHomepage ? HOMEPAGE_VERSION.OLD : HOMEPAGE_VERSION.NEW;
    setHomepageVersion(next);
    fireHomepageGtm("homepage_version_switched", {
      event_label1: next,
      event_label2: source,
    });
    if (location?.pathname !== "/home") history.push("/home");
    onSwitched?.();
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={!isNewHomepage}
      aria-label={label}
      title={compact ? label : undefined}
      onClick={handleSwitch}
      className={clsx(
        styles.switch,
        styles[layout],
        compact && styles.compact,
        isDarkMode && styles.dark,
      )}
    >
      <span className={styles.track} aria-hidden="true">
        <span className={styles.knob} />
      </span>
      {layout === "stacked" ? (
        <span className={styles.labelRow} aria-hidden="true">
          <span className={styles.labelClip}>
            <span className={styles.label}>{label}</span>
          </span>
        </span>
      ) : (
        <span className={styles.label}>{label}</span>
      )}
    </button>
  );
};

export default HomepageVersionSwitch;
